import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { Extension } from '@tiptap/core';

// Internal dependencies
import { Toolbar } from './Toolbar';
import { markdownToHtml } from '../../utils/markdown';

/**
 * Props interface for RichTextEditor component
 */
interface RichTextEditorProps {
  /** HTML content for the editor */
  content: string;
  /** Callback function when content changes */
  onChange: (content: string) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Placeholder text when editor is empty */
  placeholder?: string;
}

/**
 * Custom FontSize extension for TipTap
 * Enables font size manipulation in the editor
 */
const FontSize = Extension.create({
  name: 'fontSize',
  addAttributes() {
    return {
      size: {
        default: null,
        parseHTML: element => element.style.fontSize,
        renderHTML: attributes => {
          if (!attributes.size) return {};
          return {
            style: `font-size: ${attributes.size}`
          };
        },
      },
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize,
            renderHTML: attributes => {
              if (!attributes.fontSize) return {};
              return {
                style: `font-size: ${attributes.fontSize}`
              };
            },
          },
        },
      },
    ];
  },
});

/**
 * Rich Text Editor Component
 * A WYSIWYG editor built with TipTap that supports formatting, links, and more
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
  content, 
  onChange, 
  disabled = false,
  placeholder = 'Enter description...'
}) => {
  // Initialize TipTap editor with extensions and configuration
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure inline code styling
        code: {
          HTMLAttributes: {
            class: 'text-sm font-mono bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 px-1.5 py-0.5 rounded-md border border-gray-300 dark:border-gray-700',
          },
        },
        // Configure code block styling
        codeBlock: {
          HTMLAttributes: {
            class: 'bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 p-4 rounded-lg my-2 font-mono text-sm whitespace-pre-wrap border border-gray-200 dark:border-gray-700',
          },
        },
        // Configure list styling
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc ml-4 my-1 text-gray-800 dark:text-gray-100',
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal ml-4 my-1 text-gray-800 dark:text-gray-100',
          },
        },
        paragraph: {
          HTMLAttributes: {
            class: 'my-1 leading-relaxed text-gray-800 dark:text-gray-100',
          },
        },
      }),
      // Add additional extensions
      Underline,
      TextStyle,
      Color,
      FontSize,
      // Configure link handling with security attributes
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 hover:text-blue-700 underline',
        },
      }),
      // Configure text alignment options
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: markdownToHtml(content),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert focus:outline-none max-w-none min-h-[100px] px-4 py-2 text-gray-800 dark:text-white',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
  });

  // Update editor content when content prop changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(markdownToHtml(content), {
        emitUpdate: false,
      });
    }
  }, [content, editor]);

  return (
    <div className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
      {/* Render toolbar only when editor is enabled */}
      {!disabled && <Toolbar editor={editor} />}
      <div className="relative">
        {/* Main editor content area */}
        <EditorContent 
          editor={editor} 
          className="h-[200px] w-full focus:outline-none text-sm leading-normal overflow-y-auto text-gray-800 dark:text-white"
        />
        {/* Placeholder text */}
        {!content && !disabled && (
          <div className="absolute top-6 left-6 text-gray-500 dark:text-gray-400 pointer-events-none">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor; 